import React, {useEffect, useState} from 'react';
import {Accordion, Button, Col, Container, Form, FormLabel, Modal, Row} from "react-bootstrap";
import {useTranslation} from "react-i18next";
import useMyTranslation from "../../Hooks/useMyTranslation";
import useCompanyId from "../../Hooks/useCompanyId";
import {getZonePlanning, updateMsisdn, updateZonePlanning} from "../../API/msisdnApi";
import DatePicker from "react-datepicker";
import {faPlus} from "@fortawesome/free-solid-svg-icons";
import IconButton from "../IconButton";
import {toast} from "react-toastify";
import messageFromError from "../../utils/errorUtils";
import ConditionalRender, {Fallback} from "../ConditionalRender";

const ManageZonePlanningModal = ({msisdn, geofences, show, onHide}) => {
    const {t} = useTranslation();
    const myT = useMyTranslation('pages.workers.managePlanningDialog');
    const companyId = useCompanyId();

    const [enabled, setEnabled] = useState(msisdn.geofenceAssignmentEnabled);
    const [planning, setPlanning] = useState(null);

    useEffect(() => {
        if (show && planning === null) {
            getZonePlanning(msisdn.id, companyId).then(response => {
                setPlanning(response.data);
            });
        }
    }, [show, planning, msisdn.id, companyId]);

    const updateSlot = (geofenceId, idx, field, valueIsoOrNull) => {
        setPlanning(prev => prev.map(p =>
            p.geofenceId !== geofenceId ? p : {
                ...p,
                activeTimes: p.activeTimes.map((s, i) => i === idx ? { ...s, [field]: valueIsoOrNull } : s)
            }
        ));
    };

    const handleRemovePlanningSlot = (geofenceId, slotIndex) => {
        setPlanning(prev => prev.map(p =>
            p.geofenceId !== geofenceId ? p : {
                ...p,
                activeTimes: p.activeTimes.filter((_, i) => i !== slotIndex)
            }
        ));
    };

    const handleAddPlanningSlot = (geofenceId) => {
        const now = snapTo15(new Date());
        const in2h = snapTo15(new Date(now.getTime() + 2 * 60 * 60 * 1000));

        setPlanning(prev =>
            {
                if (!prev.some(p => p.geofenceId === geofenceId)) {
                    return [...prev, { geofenceId, activeTimes: [{ start: now.toISOString(), end: in2h.toISOString() }] }]
                }

                return prev.map(p =>
                    p.geofenceId !== geofenceId ? p : {
                        ...p,
                        activeTimes: [...p.activeTimes, { start: now.toISOString(), end: in2h.toISOString() }]
                    }
                )
            }
        );
    };

    function snapTo15(date) {
        const ms = 1000 * 60 * 15; // 15 minutes in ms
        return new Date(Math.floor(date.getTime() / ms) * ms);
    }

    const submitPlanning = async () => {
        try {
            await updateMsisdn({...msisdn, geofenceAssignmentEnabled: enabled})
            await updateZonePlanning(msisdn.id, companyId, planning);
            
            toast.success(t('pages.workers.managePlanningDialog.updateSuccess'));
            onHide();
        }
        catch (e) {
            toast.error(messageFromError(e))
        }
    };


    const PlanningForGeofence = ({geofencePlanning}) => {
        return (
            <Container className={"p-0"}>
                {geofencePlanning.activeTimes.map((slot, i) => {
                    const startDate = slot.start ? new Date(slot.start) : null;
                    const endDate   = slot.end ? new Date(slot.end) : null;

                    return (
                        <Row key={i} className={'py-1 align-items-center'}>
                            <Col className={'d-flex align-items-center'}>
                                <FormLabel className="mb-0 me-2">{t('global.time.from')}</FormLabel>
                                <DatePicker
                                    selected={startDate}
                                    onChange={(d) => updateSlot(geofencePlanning.geofenceId, i, 'start', d ? d.toISOString() : null)}
                                    showTimeSelect
                                    timeIntervals={15} // 15-minute intervals
                                    timeFormat={"HH:mm"}
                                    dateFormat="yyyy-MM-dd HH:mm"
                                    wrapperClassName={'w-100'}
                                    customInput={<Form.Control className="w-100" />}
                                />
                            </Col>
                           <Col className={'d-flex align-items-center'}>
                               <FormLabel className="mb-0 me-2">{t('global.time.to')}</FormLabel>
                               <DatePicker
                                   selected={endDate}
                                   onChange={(d) => updateSlot(geofencePlanning.geofenceId, i, 'end', d ? d.toISOString() : null)}
                                   showTimeSelect
                                   timeIntervals={15}
                                   timeFormat={"HH:mm"}
                                   dateFormat="yyyy-MM-dd HH:mm"
                                   isClearable   // allow null end
                                   wrapperClassName={'w-100'}
                                   customInput={<Form.Control className="w-100" />}
                               />
                           </Col>
                            <Col xs={3} lg={2} xl={2}>
                                <Button variant="danger" className={'w-100'} size="sm" onClick={() => handleRemovePlanningSlot(geofencePlanning.geofenceId, i)}>{t('global.operations.remove')}</Button>
                            </Col>
                        </Row>
                    );
                })}
                <ConditionalRender condition={geofencePlanning.activeTimes.length === 0}>
                    <p className="text-muted text-center">{myT('workLocationNotPlanned')}.</p>
                </ConditionalRender>

                <div className={'mt-3 center'}>
                    <IconButton icon={faPlus} color="white" bg="#198754" onClick={() => handleAddPlanningSlot(geofencePlanning.geofenceId)}/>
                </div>
            </Container>
        );
    };

    return (
        <Modal show={show} onHide={onHide} size="xl">
            <Modal.Header closeButton className="modal-header">
                <Modal.Title>{myT("title")}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className="d-flex align-items-center">
                    <FormLabel className="mb-0 me-2 ps-0 mt-1">{myT("zonePlanning")}</FormLabel>
                    <Form.Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
                </div>
                <p className="text-muted">{myT('zonePlanningDesc')}</p>
                <hr/>

                <ConditionalRender condition={enabled}>
                    <Accordion alwaysOpen>
                        {geofences.map((geofence, i) =>
                            {
                                if (!planning)
                                    return null;

                                const geofencePlanning = planning.find(p => p.geofenceId === geofence.id) ?? { geofenceId: geofence.id, activeTimes: [] };
                                return (
                                    <Accordion.Item eventKey={String(i)} key={geofence.id}>
                                        <Accordion.Header>
                                            {geofence.name}
                                            <ConditionalRender condition={geofencePlanning.activeTimes.length > 0}>
                                                <span className="badge bg-success ms-2">{geofencePlanning.activeTimes.length}</span>
                                            </ConditionalRender>
                                        </Accordion.Header>
                                        <Accordion.Body>
                                            <PlanningForGeofence geofencePlanning={geofencePlanning} />
                                        </Accordion.Body>
                                    </Accordion.Item>
                                )
                            }
                        )}
                    </Accordion>

                    <Fallback>
                        <p className={'h3 text-center mb-0 text-secondary'}>{myT('planningNotEnabled')}</p>
                    </Fallback>
                </ConditionalRender>

            </Modal.Body>

            <Modal.Footer>
            <Button variant="success" onClick={submitPlanning}>
                    {t('global.operations.submit')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ManageZonePlanningModal;
